import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LayoutGrid, ShieldCheck, UserCog } from 'lucide-react';
import { get, post, put } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { MODULE_CATALOGUE, type ModuleCode } from '@his/shared';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Badge, Button, Checkbox, Field, Input, Textarea } from '@/components/ui/primitives';
import { ChoiceButtons } from '@/components/ui/choice-buttons';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import type { PermissionGroup, Role } from './types';

const SCOPES = [
  { value: 'tenant', label: 'The whole facility' },
  { value: 'branch', label: 'Their own branch' },
  { value: 'department', label: 'Their own department' },
  { value: 'self', label: 'Only their own records' },
];

export const ROLES_RETURN_TO = '/people?tab=roles';

function blankRole() {
  return {
    code: '',
    name: '',
    description: '',
    scope: 'branch',
    require2fa: false,
    permissions: [] as string[],
    modules: [] as string[],
  };
}

function roleToForm(role: Role | null) {
  if (!role) return blankRole();
  return {
    code: role.code,
    name: role.name,
    description: role.description ?? '',
    scope: role.scope,
    require2fa: role.require2fa,
    permissions: [...role.permissions],
    modules: [...role.modules],
  };
}

export function RolePage() {
  const { roleId = '' } = useParams();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), ROLES_RETURN_TO);

  const roles = useQuery({
    queryKey: ['roles'],
    queryFn: () => get<Role[]>('/org/roles'),
    enabled: Boolean(roleId),
  });
  const role = roles.data?.find((row) => row.id === roleId) ?? null;

  if (roleId && !role) {
    return (
      <FormScreen title={tr('admin', 'loading_roles')} icon={UserCog} backTo={returnTo} wide>
        <RecordState error={roles.error} missing={Boolean(roles.data)} label={tr('admin', 'loading_roles')} />
      </FormScreen>
    );
  }

  return <RoleForm key={roleId || 'new'} role={role} returnTo={returnTo} />;
}

function RoleForm({ role, returnTo }: { role: Role | null; returnTo: string }) {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [initial] = useState(() => roleToForm(role));
  const [form, setForm] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [advanced, setAdvanced] = useState(false);
  const builtIn = Boolean(role?.isSystem);

  const catalogue = useQuery({
    queryKey: ['permission-catalogue'],
    queryFn: () => get<PermissionGroup[]>('/org/permissions'),
  });
  const licensedModules = new Set((catalogue.data ?? []).filter((group) => group.state !== 'read_only').map((group) => group.module));

  const save = useMutation({
    mutationFn: () => {
      const guard = role?.updatedAt ? { headers: { 'If-Match': role.updatedAt } } : {};
      if (role && builtIn) return put(`/org/roles/${role.id}/permissions`, { permissions: form.permissions }, guard);
      const body = {
        code: role ? form.code : form.code.trim() || undefined,
        name: form.name,
        description: form.description || undefined,
        scope: form.scope,
        require2fa: form.require2fa,
        permissions: form.permissions,
        modules: form.modules,
      };
      return role ? put(`/org/roles/${role.id}`, body, guard) : post('/org/roles', body);
    },
    onSuccess: () => {
      toast.success(role ? tr('admin', 'role_updated') : tr('admin', 'role_created'));
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void queryClient.invalidateQueries({ queryKey: ['role-groups'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const toggle = (code: string) =>
    setForm((current) => ({
      ...current,
      permissions: current.permissions.includes(code)
        ? current.permissions.filter((p) => p !== code)
        : [...current.permissions, code],
    }));

  const missing: string[] = [];
  if (!form.name.trim()) missing.push('a name');
  if (!role && form.code.trim() && !/^[a-z0-9_]{2,40}$/.test(form.code)) {
    missing.push('a code of 2–40 lowercase letters, digits or underscores');
  }
  if (role && role.userCount > 0 && form.permissions.length === 0) {
    missing.push('at least one permission, because staff hold this role');
  }

  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(initial);

  return (
    <FormScreen
      title={role ? tr('admin', 'edit_x', { name: role.name }) : tr('admin', 'add_a_role')}
      description={tr('admin', 'what_this_role_may_do_and_how_far_its_reach_goes')}
      icon={UserCog}
      backTo={returnTo}
      dirty={dirty}
      wide
      onSubmit={(event) => {
        event.preventDefault();
        if (missing.length > 0) return;
        setError(null);
        save.mutate();
      }}
    >
      {role?.isSystem && <Alert variant="warning">{tr('admin', 'this_is_a_standard_role_you_can_change_which')}</Alert>}

      <FormSection title={tr('admin', 'role_2')} icon={UserCog}>
        <Field label={tr('admin', 'name')} required>
          <Input value={form.name} disabled={builtIn} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        {role || advanced ? (
          <Field
            label={tr('admin', 'code')}
            hint={role ? tr('admin', 'fixed_once_the_role_exists') : tr('admin', 'optional_lowercase_no_spaces_made_from_the_name')}
          >
            <Input
              value={form.code}
              disabled={Boolean(role)}
              onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
            />
          </Field>
        ) : (
          <div className="flex items-end pb-2">
            <button type="button" className="text-xs text-primary hover:underline focus-ring" onClick={() => setAdvanced(true)}>
              {tr('admin', 'advanced_choose_the_role_code')}
            </button>
          </div>
        )}
        <Field label={tr('admin', 'description')} className="sm:col-span-2">
          <Textarea
            rows={2}
            value={form.description}
            disabled={builtIn}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
        </Field>
        <Field label={tr('admin', 'how_far_it_reaches')} hint={tr('admin', 'what_the_holder_may_see_beyond_their_own_work')}>
          <ChoiceButtons
            layout="chips"
            value={form.scope}
            disabled={builtIn}
            onChange={(value) => setForm({ ...form, scope: value })}
            options={SCOPES}
          />
        </Field>
        <div className="flex items-end pb-2">
          <Checkbox
            checked={form.require2fa}
            disabled={builtIn}
            onCheckedChange={(checked) => setForm({ ...form, require2fa: checked })}
            label={tr('admin', 'require_two_factor_sign_in')}
          />
        </div>
      </FormSection>

      <FormSection
        title={tr('admin', 'screens_this_role_works_in')}
        description={tr('admin', 'pick_the_modules_whose_screens_the_holder')}
        icon={LayoutGrid}
        columns={1}
      >
        <div className="flex flex-wrap gap-1.5">
          {MODULE_CATALOGUE.filter((m) => m.code !== 'core' && (licensedModules.has(m.code) || form.modules.includes(m.code))).map((m) => (
            <Button
              key={m.code}
              type="button"
              size="sm"
              disabled={builtIn}
              variant={form.modules.includes(m.code) ? 'default' : 'outline'}
              onClick={() =>
                setForm({
                  ...form,
                  modules: form.modules.includes(m.code)
                    ? form.modules.filter((code) => code !== m.code)
                    : [...form.modules, m.code as ModuleCode],
                })
              }
            >
              {m.name}
            </Button>
          ))}
        </div>
      </FormSection>

      <FormSection
        title={`${tr('admin', 'permissions')} (${form.permissions.length} ${tr('admin', 'selected')})`}
        icon={ShieldCheck}
        columns={1}
      >
        <div className="space-y-4">
          {catalogue.isLoading && <p className="text-sm text-muted-foreground">{tr('admin', 'loading_the_catalogue')}</p>}
          {catalogue.data?.map((group) => {
            const codes = group.permissions.filter((p) => p.state !== 'read_only').map((p) => p.code);
            const all = codes.length > 0 && codes.every((code) => form.permissions.includes(code));
            return (
              <div key={`${group.module}:${group.group}`}>
                <div className="mb-1 flex items-center gap-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{group.group}</p>
                  <Badge variant="muted">{group.moduleLabel ?? group.module}</Badge>
                  {group.state === 'read_only' && <Badge variant="warning">{tr('admin', 'archived')}</Badge>}
                  <button
                    type="button"
                    hidden={codes.length === 0}
                    className="ms-auto text-xs text-primary hover:underline focus-ring"
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        permissions: all
                          ? current.permissions.filter((code) => !codes.includes(code))
                          : [...new Set([...current.permissions, ...codes])],
                      }))
                    }
                  >
                    {all ? tr('admin', 'none') : tr('admin', 'all')}
                  </button>
                </div>
                <div className="grid gap-1 sm:grid-cols-2">
                  {group.permissions.map((permission) => (
                    <Checkbox
                      key={permission.code}
                      checked={form.permissions.includes(permission.code)}
                      disabled={permission.state === 'read_only' && !form.permissions.includes(permission.code)}
                      onCheckedChange={() => toggle(permission.code)}
                      label={
                        <>
                          {permission.description}
                          {permission.sensitive && (
                            <Badge variant="warning" className="ms-1">
                              {tr('admin', 'sensitive')}
                            </Badge>
                          )}
                        </>
                      }
                      description={<span className="font-mono text-[11px]">{permission.code}</span>}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions
        submitLabel={role ? tr('admin', 'save_changes') : tr('admin', 'create_role')}
        loading={save.isPending}
        incomplete={missing.length > 0}
        cancelTo={returnTo}
        note={missing.length > 0 ? `${tr('admin', 'still_needed')} ${missing.join(', ')}` : undefined}
      />
    </FormScreen>
  );
}
